import {createAction, handleActions} from 'redux-actions';
import * as api from '../services/api';
import _ from 'lodash';
import { actions as labelCollection} from './labelCollection';
import { actions as language} from './language';
import {handleFetch} from "../services/api";

// regular actions
const SET = '@translationtool/labels/SET';
const SELECT = '@translationtool/labels/SELECT';
const HIGHLIGHT = '@translationtool/labels/HIGHLIGHT';
const RESET_SELECTED = '@translationtool/labels/RESET_SELECTED';
const REMOVE = '@translationtool/labels/REMOVE';
const REPLACE_TRANSLATION = '@translationtool/labels/REPLACE_TRANSLATION';
const REPLACE_TRANSLATIONS = '@translationtool/labels/REPLACE_TRANSLATIONS';

const set  = createAction(SET, labels => labels);
const select  = createAction(SELECT, label => label);
const highlight  = createAction(HIGHLIGHT, label => label);
const resetSelected  = createAction(RESET_SELECTED);
const remove  = createAction(REMOVE, label => label);
const replaceTranslation  = createAction(REPLACE_TRANSLATION, (labelName, translation) => {
	return {labelName, translation};
});
const replaceTranslations  = createAction(REPLACE_TRANSLATIONS, (languageIdentifier, translations) => {
	return {languageIdentifier, translations};
});

export const reducer = handleActions(
	{
		[SET]: (state, action) => {
			return {...state, labels: action.payload};
		},
		[SELECT]: (state, action) => {
			return {...state, selected: action.payload};
		},
		[HIGHLIGHT]: (state, action) => {
			return {...state, highlighted: action.payload};
		},
		[RESET_SELECTED]: (state) => {
			return {...state, selected: null};
		},
		[REMOVE]: (state, action) => {
			return {...state, labels: _.without(state.labels, _.find(state.labels, {name: action.payload.name}))};
		},
		[REPLACE_TRANSLATION]: (state, action) => {
			const newState = _.cloneDeep(state);

			_.chain(newState)
				.get('labels')
				.find({name: action.payload.labelName})
				.set(
					['translations', action.payload.translation.languageIdentifier, action.payload.translation.pluralKey],
					_.omit(action.payload.translation, ['languageIdentifier', 'pluralKey'])
				)
				.value();

			return newState;
		},
		[REPLACE_TRANSLATIONS]: (state, action) => {
			const newState = _.cloneDeep(state);

			let labels = _.get(newState, 'labels');
			_.each(action.payload.translations, translation => {
				_.chain(labels)
					.find({name: translation.labelName})
					.set(
						['translations', action.payload.languageIdentifier, translation.pluralKey],
						{newValue: '', value: translation.value}
					)
					.value();
			});

			return newState;
		}
	},
	{
		labels: [],
		selected: null,
		highlighted: null
	}
);

// thunks
const selectWrapper = (name) => (dispatch, getState) => {
	return api.handleFetch(
		() => api.fetchLabel(encodeURIComponent(getState().project.selected.name), encodeURIComponent(getState().labelCollection.selected.identifier), name),
		dispatch,
		label => dispatch(select(label)),
		'Could not fetch label "' + name + '"'
	);
};

const fetch = () => (dispatch, getState) => {
	return api.handleFetch(
		() => api.fetchLabels(encodeURIComponent(getState().project.selected.name), encodeURIComponent(getState().labelCollection.selected.identifier)),
		dispatch,
		labels => dispatch(set(labels)),
		'No labels found'
	);
};

const fetchAll = () => (dispatch, getState) => {
    return api.handleFetch(
        () => api.fetchAllLabels(encodeURIComponent(getState().project.selected.name)),
        dispatch,
        labels => dispatch(set(labels)),
        'No labels found...'
    )
}

const create = (label) => (dispatch, getState) => {
	return api.handleUpdate(
		() => api.createLabel(encodeURIComponent(getState().project.selected.name), encodeURIComponent(getState().labelCollection.selected.identifier), label),
		dispatch,
		() => {
		    dispatch(fetch())
            dispatch(labelCollection.fetch())
		}
	);
};

const update = (label) => (dispatch, getState) => {
	const name = getState().label.selected.name;
	return api.handleUpdate(
		() => api.updateLabel(encodeURIComponent(getState().project.selected.name), encodeURIComponent(getState().labelCollection.selected.identifier), encodeURIComponent(name), label),
		dispatch,
		() => {
		    dispatch(fetch())
            dispatch(labelCollection.fetch())
		}
	);
};

const updateNoValueLabel = (label) => (dispatch, getState) => {
	const name = label.name;
	return api.handleUpdate(
		() => api.updateNoValueLabel(encodeURIComponent(getState().project.selected.name), encodeURIComponent(getState().labelCollection.selected.identifier), encodeURIComponent(name), !label.noValue),
		dispatch,
		() => dispatch(fetch())
	);
};

const deleteLabel = (label) => (dispatch, getState) => {
	return api.handleUpdate(
		() => api.deleteLabel(encodeURIComponent(getState().project.selected.name), encodeURIComponent(getState().labelCollection.selected.identifier), encodeURIComponent(label.name)),
		dispatch,
		() => {
		    dispatch(remove(label))
            dispatch(labelCollection.fetch())
		}
	);
};

const fetchTranslation = (labelName, languageIdentifier, pluralKey)  => (dispatch, getState) => {
	return api.handleFetch(
		() => api.fetchTranslations(
			encodeURIComponent(getState().project.selected.name),
			encodeURIComponent(getState().labelCollection.selected.identifier),
			encodeURIComponent(labelName),
			encodeURIComponent(languageIdentifier),
			encodeURIComponent(pluralKey)
		),
		dispatch,
		translation => dispatch(replaceTranslation(labelName, translation)),
		'Translation not found'
	);
};

const updateTranslation = (labelName, languageIdentifier, pluralKey, translation) => (dispatch, getState) => {
    dispatch(language.setLoading(languageIdentifier))
	return api.handleUpdate(
		() => api.updateTranslation(
			encodeURIComponent(getState().project.selected.name),
			encodeURIComponent(getState().labelCollection.selected.identifier),
			encodeURIComponent(labelName),
			encodeURIComponent(languageIdentifier),
			encodeURIComponent(pluralKey),
			translation
		),
		dispatch,
		() => {
            dispatch(language.removeLoading(languageIdentifier))
            dispatch(fetchTranslation(labelName, languageIdentifier, pluralKey))
        }
	);
};

const fetchAllTranslationsForLanguage = languageIdentifier => (dispatch, getState) => {
	api.handleFetch(
		() => api.translationsForLanguage(
			encodeURIComponent(getState().project.selected.name),
			encodeURIComponent(getState().labelCollection.selected.identifier),
			encodeURIComponent(languageIdentifier)
		),
		dispatch,
		translations => dispatch(replaceTranslations(languageIdentifier, translations))

	)
};


// public actions
export const actions = {
	fetch,
    fetchAll,
	create,
	update,
	updateNoValueLabel,
	delete: deleteLabel,
	select: selectWrapper,
	highlightLabel: highlight,
	resetSelected,
	updateTranslation,
	fetchAllTranslationsForLanguage
};



// WEBPACK FOOTER //
// ./React/src/reducers/label.js